import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { img, type LocationCtx } from "./location-data";

const photos = [
  { id: 13, category: "Installation", caption: "Engineer working on Fujitsu outdoor units" },
  { id: 15, category: "Installation", caption: "Daikin Sky Air outdoor unit" },
  { id: 16, category: "Installation", caption: "Twin Daikin outdoor systems" },
  { id: 5, category: "Installation", caption: "Ceiling cassette air conditioning" },
  { id: 7, category: "Installation", caption: "Ceiling cassette and associated pipework" },
  { id: 3, category: "Installation", caption: "Wall-mounted system controllers" },
  { id: 6, category: "Installation", caption: "Multi-system control panel" },
  { id: 10, category: "Installation", caption: "Commercial outdoor unit and pipework" },
  { id: 12, category: "Maintenance & repair", caption: "Refrigerant pressure testing" },
  { id: 11, category: "Maintenance & repair", caption: "Engineer accessing outdoor unit components" },
  { id: 4, category: "Maintenance & repair", caption: "Outdoor unit with service panel open" },
  { id: 17, category: "Maintenance & repair", caption: "Engineer preparing a replacement air filter" },
  { id: 18, category: "Maintenance & repair", caption: "Commercial rooftop air conditioning equipment" },
  { id: 19, category: "Maintenance & repair", caption: "Rooftop HVAC plant and service access" },
  { id: 20, category: "Maintenance & repair", caption: "Rooftop unit with components exposed for servicing" },
  { id: 21, category: "Maintenance & repair", caption: "Outdoor condensers and rooftop ventilation equipment" },
  { id: 22, category: "Maintenance & repair", caption: "Commercial rooftop HVAC systems" },
  { id: 23, category: "Maintenance & repair", caption: "Condensers and ductwork on a commercial roof" },
  { id: 24, category: "Maintenance & repair", caption: "Engineer inspecting an open air handling unit" },
  { id: 25, category: "Maintenance & repair", caption: "Air filter replacement during routine servicing" },
  { id: 26, category: "Maintenance & repair", caption: "Engineer attending rooftop HVAC equipment" },
  { id: 8, category: "Ventilation", caption: "Commercial ventilation fan assembly" },
  { id: 1, category: "Supply", caption: "Insulated ductwork loaded for delivery" },
  { id: 2, category: "Supply", caption: "Commercial air conditioning equipment on site" },
  { id: 9, category: "Supply", caption: "Outdoor air conditioning equipment" },
  { id: 14, category: "Supply", caption: "Portable cooling equipment" },
];
const categories = ["All work", "Installation", "Maintenance & repair", "Ventilation", "Supply"];

function initialCategory(ctx: LocationCtx) {
  const service = `${ctx.service.slug ?? ""} ${ctx.service.title}`.toLowerCase();
  if (/vent|duct/.test(service)) return "Ventilation";
  if (/repair|mainten|servicing/.test(service)) return "Maintenance & repair";
  if (/supply/.test(service)) return "Supply";
  if (/install/.test(service)) return "Installation";
  return "All work";
}

export function ProjectGallery({ ctx }: { ctx: LocationCtx }) {
  const [category, setCategory] = useState(() => initialCategory(ctx));
  const visible = photos.filter(photo => category === "All work" || photo.category === category);
  return (
    <section className="bg-muted/30 py-12 md:py-16" aria-labelledby="project-gallery-heading">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary">Our work in pictures</p>
        <h2 id="project-gallery-heading" className="mt-3 font-display text-3xl md:text-4xl font-bold">A closer look at our services</h2>
        <p className="mt-3 text-muted-foreground">Explore our installation, supply, ventilation and servicing photos. Select a photo to view it in full.</p>
        <div className="my-6 flex flex-wrap gap-2" aria-label="Filter project photos">
          {categories.map(item => (
            <button key={item} type="button" aria-pressed={category === item} onClick={() => setCategory(item)}
              className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary ${category === item ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background hover:bg-muted"}`}>
              {item}
            </button>
          ))}
        </div>
        <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory" aria-label={`${category} photos`}>
          {visible.map(photo => (
            <Dialog key={photo.id}>
              <DialogTrigger asChild>
                <button type="button" className="group w-64 sm:w-72 shrink-0 snap-start overflow-hidden rounded-2xl border border-border bg-card text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary" aria-label={`View photo: ${photo.caption}`}>
                  <img src={img(`project-${photo.id}.webp`)} alt={photo.caption} loading="lazy" decoding="async" width={384} height={288} className="aspect-[4/3] w-full object-cover transition-transform group-hover:scale-105" />
                  <span className="block p-4 text-sm font-medium">{photo.caption}</span>
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl">
                <DialogTitle className="pr-6">{photo.caption}</DialogTitle>
                <DialogDescription>{photo.category} — VertexAir project gallery</DialogDescription>
                <img src={img(`project-${photo.id}.webp`)} alt={photo.caption} className="max-h-[70vh] w-full object-contain" />
              </DialogContent>
            </Dialog>
          ))}
        </div>
        <p className="mt-2 text-sm text-muted-foreground">Scroll sideways to see more photos.</p>
      </div>
    </section>
  );
}
